import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClockIcon, PauseIcon, PlayIcon, StopIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { useBillingStore } from '../store/billingStore';
import { useBilling, TimerConflictError } from '../hooks/useBilling';
import type { TimeEntry, TimeSettings } from '../types/billing.types';
import ClientMatterPicker from '../../../components/shared/ClientMatterPicker';
import type { ClientMatterSelection } from '../../../components/shared/ClientMatterPicker';

/** How often the header re-reads GET /time-entries/running. */
export const RUNNING_POLL_MS = 30_000;
/** Placeholder narrative for an entry created from the header Start button. */
export const TIMER_PLACEHOLDER_DESCRIPTION = 'Timer';

/**
 * Seconds the server has already banked on the entry. The backend adds each
 * paused segment into `duration` (hours); the running segment is measured from
 * `timer_started_at`, or from the last start/resume audit event when the
 * timestamp is missing.
 */
export function computeElapsedSeconds(entry: TimeEntry, nowMs: number): number {
  const banked = Math.max(0, Number(entry.duration) || 0) * 3600;
  if (entry.timer_status !== 'running') return Math.round(banked);
  let startedAt: number | null = entry.timer_started_at ? Date.parse(entry.timer_started_at) : null;
  if (startedAt == null || Number.isNaN(startedAt)) {
    const lastStart = [...(entry.timer_log ?? [])]
      .reverse()
      .find((ev) => ev.action === 'start' || ev.action === 'resume');
    startedAt = lastStart ? Date.parse(lastStart.timestamp) : null;
  }
  if (startedAt == null || Number.isNaN(startedAt)) return Math.round(banked);
  return Math.round(banked + Math.max(0, nowMs - startedAt) / 1000);
}

export function formatElapsed(totalSeconds: number): string {
  const total = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

const matterLabelFor = (entry: TimeEntry): string => {
  const num = entry.matter?.matter_number || '';
  const desc = entry.matter?.description || '';
  if (num) return desc ? `${num} — ${desc}` : num;
  return entry.description || 'Time entry';
};

const iconButton =
  'p-1 rounded text-[var(--lex-text-tertiary)] hover:bg-[var(--lex-surface-hover)] hover:text-[var(--lex-text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--lex-focus-ring)] disabled:opacity-50 transition-colors';

/**
 * Persistent header timer (W1.6). Mounted only when the firm's time settings
 * enable it. The server owns the timer; this widget reflects GET /running and
 * drives start / pause / resume / stop.
 */
const GlobalTimer: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const store = useBillingStore();
  const {
    getTimeSettings,
    getRunningTimer,
    startTimer,
    pauseTimer,
    stopTimer,
    createTimeEntry,
  } = useBilling();

  const [settings, setSettings] = useState<TimeSettings | null>(null);
  const [entry, setEntry] = useState<TimeEntry | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getTimeSettings()
      .then((s) => { if (!cancelled) setSettings(s); })
      .catch(() => { /* widget stays unmounted */ });
    return () => { cancelled = true; };
  }, [getTimeSettings]);

  const enabled = Boolean(settings?.enable_global_timer);

  const refresh = useCallback(async () => {
    try {
      const running = await getRunningTimer();
      if (mountedRef.current) setEntry(running);
    } catch (err) {
      if (mountedRef.current) setMessage(err instanceof Error ? err.message : 'Could not read the running timer');
    }
  }, [getRunningTimer]);

  // Poll every 30 s and on window focus while enabled.
  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const id = window.setInterval(() => { void refresh(); }, RUNNING_POLL_MS);
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, refresh]);

  // Tick the clock once a second while a timer runs.
  const isRunning = entry?.timer_status === 'running';
  useEffect(() => {
    if (!isRunning) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isRunning]);

  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
    } catch (err) {
      if (err instanceof TimerConflictError) {
        setMessage(err.message || 'Another timer is already running.');
        await refresh();
      } else {
        setMessage(err instanceof Error ? err.message : 'Timer action failed');
      }
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [refresh]);

  const handlePause = () => {
    if (!entry) return;
    void run(async () => { setEntry(await pauseTimer(entry.time_entry_id)); });
  };

  const handleResume = () => {
    if (!entry) return;
    void run(async () => { setEntry(await startTimer(entry.time_entry_id)); });
  };

  /** Stop, put the stopped entry in the list, and open the drawer on it for narrative / UTBMS. */
  const openEntryInDrawer = useCallback((stopped: TimeEntry) => {
    const state = useBillingStore.getState();
    const present = state.timeEntries.some((e) => e.time_entry_id === stopped.time_entry_id);
    const list = present
      ? state.timeEntries.map((e) => (e.time_entry_id === stopped.time_entry_id ? stopped : e))
      : [stopped, ...state.timeEntries];
    state.setTimeEntries(list, {
      ...state.timeEntryPagination,
      total: present ? state.timeEntryPagination.total : state.timeEntryPagination.total + 1,
    });
    state.setActiveTab('time-entries');
    state.openDrawer('time-entry', 'edit', stopped.time_entry_id);
    navigate('/billing');
  }, [navigate]);

  const handleStop = () => {
    if (!entry) return;
    void run(async () => {
      const stopped = await stopTimer(entry.time_entry_id);
      setEntry(null);
      openEntryInDrawer(stopped);
    });
  };

  const handleMatterSelect = (selection: ClientMatterSelection) => {
    setShowPicker(false);
    if (!selection.matterId) return;
    void run(async () => {
      const created = await createTimeEntry({
        matter_id: selection.matterId,
        entry_date: new Date().toISOString().split('T')[0],
        description: TIMER_PLACEHOLDER_DESCRIPTION,
        duration: 0,
        entry_type: 'hourly',
      });
      setEntry(await startTimer(created.time_entry_id));
    });
  };

  if (!enabled) return null;

  const elapsed = entry ? formatElapsed(computeElapsedSeconds(entry, now)) : null;

  return (
    <div className="relative flex items-center gap-1 mr-2" data-testid="global-timer">
      {entry ? (
        <div
          className="flex items-center gap-2 px-2 py-1 rounded-full border border-[var(--lex-border)] bg-[var(--lex-surface-hover)] text-sm"
          title={entry.timer_status === 'paused' ? 'Timer paused' : 'Timer running'}
        >
          <span
            className={`inline-block h-2 w-2 rounded-full ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`}
            aria-hidden
          />
          <span className="font-mono tabular-nums text-[var(--lex-text-primary)]" aria-label="Elapsed time">
            {elapsed}
          </span>
          <span className="hidden md:inline max-w-[180px] truncate text-[var(--lex-text-secondary)]">
            {matterLabelFor(entry)}
          </span>
          {isRunning ? (
            <button type="button" onClick={handlePause} disabled={busy} className={iconButton} title="Pause timer" aria-label="Pause timer">
              <PauseIcon className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={handleResume} disabled={busy} className={iconButton} title="Resume timer" aria-label="Resume timer">
              <PlayIcon className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={handleStop} disabled={busy} className={iconButton} title="Stop timer" aria-label="Stop timer">
            <StopIcon className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => { setMessage(null); setShowPicker((v) => !v); }}
          disabled={busy || store.loading}
          className="flex items-center gap-1 px-2 py-1 rounded-full text-sm text-[var(--lex-text-secondary)] hover:bg-[var(--lex-surface-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--lex-focus-ring)] disabled:opacity-50 transition-colors"
          title="Start timer"
          aria-label="Start timer"
        >
          <ClockIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Start timer</span>
        </button>
      )}

      {message && (
        <div
          role="status"
          className="absolute top-full right-0 mt-1 z-50 flex items-start gap-2 px-3 py-2 rounded-lg border border-[var(--lex-border)] bg-[var(--lex-surface-header)] text-xs text-[var(--lex-text-secondary)] shadow-lg max-w-xs"
        >
          <span>{message}</span>
          <button type="button" onClick={() => setMessage(null)} className={iconButton} aria-label="Dismiss">
            <XMarkIcon className="h-3 w-3" />
          </button>
        </div>
      )}

      {showPicker && (
        <div className="absolute top-full right-0 mt-1 z-50 w-[360px] rounded-lg border border-[var(--lex-border)] bg-[var(--lex-surface-header)] shadow-xl p-2">
          <ClientMatterPicker
            context={{ host: 'billing' }}
            onSelect={handleMatterSelect}
            onCancel={() => setShowPicker(false)}
            variant="lex"
          />
        </div>
      )}
    </div>
  );
};

export default GlobalTimer;
